import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from 'react';

const HANDLE_PX = 10;

const STAGE_DEFAULTS = {
  storageKey: 'fm.studio.stream.colWidthPx',
  defaultWidthPx: 352, // ~22rem
  minLeftPx: 256, // ~16rem
  minRightPx: 280,
  maxStoredPx: 720,
  defaultRatio: null,
  anchor: 'start',
} as const;

export interface ColumnResizeOptions {
  layoutKey: string;
  storageKey?: string;
  defaultWidthPx?: number;
  /** Fraction of the layout assigned to the stored edge when nothing is saved. */
  defaultRatio?: number;
  minLeftPx?: number;
  minRightPx?: number;
  maxStoredPx?: number;
  /** `end` stores the right column (e.g. people list). */
  anchor?: 'start' | 'end';
}

export interface ColumnResizeResult {
  layoutRef: RefObject<HTMLDivElement>;
  columnWidthPx: number;
  isDragging: boolean;
  onHandlePointerDown: (event: ReactPointerEvent<HTMLButtonElement>) => void;
}

interface ResolvedColumnResizeOptions {
  layoutKey: string;
  storageKey: string;
  defaultWidthPx: number;
  defaultRatio: number | null;
  minLeftPx: number;
  minRightPx: number;
  maxStoredPx: number;
  anchor: 'start' | 'end';
}

function resolveOptions(input: string | ColumnResizeOptions): ResolvedColumnResizeOptions {
  if (typeof input === 'string') {
    return { layoutKey: input, ...STAGE_DEFAULTS };
  }
  return {
    ...STAGE_DEFAULTS,
    ...input,
    defaultRatio: input.defaultRatio ?? null,
    anchor: input.anchor ?? 'start',
  };
}

function readStoredWidth(options: ResolvedColumnResizeOptions): number | null {
  if (typeof window === 'undefined') {
    return options.defaultRatio === null ? options.defaultWidthPx : null;
  }
  try {
    const raw = window.localStorage.getItem(options.storageKey);
    if (!raw) {
      return options.defaultRatio === null ? options.defaultWidthPx : null;
    }
    const parsed = Number.parseInt(raw, 10);
    if (!Number.isFinite(parsed)) {
      return options.defaultRatio === null ? options.defaultWidthPx : null;
    }
    const minStored = options.anchor === 'end' ? options.minRightPx : options.minLeftPx;
    return Math.min(Math.max(parsed, minStored), options.maxStoredPx);
  } catch {
    return options.defaultRatio === null ? options.defaultWidthPx : null;
  }
}

function clampStartWidth(
  widthPx: number,
  layoutWidth: number,
  minLeftPx: number,
  minRightPx: number,
): number {
  const maxLeft = Math.max(minLeftPx, layoutWidth - minRightPx - HANDLE_PX);
  return Math.min(Math.max(widthPx, minLeftPx), maxLeft);
}

function clampEndWidth(
  widthPx: number,
  layoutWidth: number,
  minLeftPx: number,
  minRightPx: number,
): number {
  const maxRight = Math.max(minRightPx, layoutWidth - minLeftPx - HANDLE_PX);
  return Math.min(Math.max(widthPx, minRightPx), maxRight);
}

function clampStoredWidth(
  widthPx: number,
  layoutWidth: number,
  options: Pick<ResolvedColumnResizeOptions, 'anchor' | 'minLeftPx' | 'minRightPx'>,
): number {
  if (options.anchor === 'end') {
    return clampEndWidth(widthPx, layoutWidth, options.minLeftPx, options.minRightPx);
  }
  return clampStartWidth(widthPx, layoutWidth, options.minLeftPx, options.minRightPx);
}

/**
 * Horizontal split resize for studio columns.
 * `start` stores left width; `end` stores right width.
 */
export function useColumnResize(layoutKeyOrOptions: string | ColumnResizeOptions): ColumnResizeResult {
  const options = resolveOptions(layoutKeyOrOptions);
  const { layoutKey, storageKey, minLeftPx, minRightPx, defaultRatio, defaultWidthPx, anchor } =
    options;
  const layoutRef = useRef<HTMLDivElement>(null);
  const hasUserSizeRef = useRef(false);
  const [columnWidthPx, setColumnWidthPx] = useState(() => {
    const stored = readStoredWidth(options);
    hasUserSizeRef.current = stored !== null;
    return stored ?? 0;
  });
  const [isDragging, setIsDragging] = useState(false);
  const dragOffsetRef = useRef(0);

  useEffect(() => {
    const layout = layoutRef.current;
    if (!layout || typeof ResizeObserver === 'undefined') {
      return;
    }

    const apply = (): void => {
      const width = layout.getBoundingClientRect().width;
      setColumnWidthPx((current) => {
        if (!hasUserSizeRef.current && defaultRatio !== null) {
          return clampStoredWidth(width * defaultRatio, width, { anchor, minLeftPx, minRightPx });
        }
        const fallback = current > 0 ? current : defaultWidthPx;
        return clampStoredWidth(fallback, width, { anchor, minLeftPx, minRightPx });
      });
    };

    const observer = new ResizeObserver(() => {
      apply();
    });
    observer.observe(layout);
    apply();
    return () => {
      observer.disconnect();
    };
  }, [anchor, defaultRatio, defaultWidthPx, layoutKey, minLeftPx, minRightPx]);

  const onHandlePointerDown = useCallback(
    (event: ReactPointerEvent<HTMLButtonElement>) => {
      if (event.button !== 0) {
        return;
      }

      const layout = layoutRef.current;
      if (!layout) {
        return;
      }

      event.preventDefault();
      const handle = event.currentTarget;
      handle.setPointerCapture(event.pointerId);
      const origin = layout.getBoundingClientRect();
      dragOffsetRef.current =
        anchor === 'end'
          ? origin.right - event.clientX - columnWidthPx
          : event.clientX - origin.left - columnWidthPx;
      setIsDragging(true);

      const onMove = (moveEvent: PointerEvent): void => {
        const box = layout.getBoundingClientRect();
        const next =
          anchor === 'end'
            ? box.right - moveEvent.clientX - dragOffsetRef.current
            : moveEvent.clientX - box.left - dragOffsetRef.current;
        setColumnWidthPx(clampStoredWidth(next, box.width, { anchor, minLeftPx, minRightPx }));
      };

      const onUp = (upEvent: PointerEvent): void => {
        handle.releasePointerCapture(upEvent.pointerId);
        handle.removeEventListener('pointermove', onMove);
        handle.removeEventListener('pointerup', onUp);
        handle.removeEventListener('pointercancel', onUp);
        setIsDragging(false);
        hasUserSizeRef.current = true;
        setColumnWidthPx((current) => {
          try {
            window.localStorage.setItem(storageKey, String(Math.round(current)));
          } catch {
            // ignore quota / private mode
          }
          return current;
        });
      };

      handle.addEventListener('pointermove', onMove);
      handle.addEventListener('pointerup', onUp);
      handle.addEventListener('pointercancel', onUp);
    },
    [anchor, columnWidthPx, minLeftPx, minRightPx, storageKey],
  );

  return {
    layoutRef,
    columnWidthPx,
    isDragging,
    onHandlePointerDown,
  };
}
